import { Injectable } from '@angular/core';
import { MswipeService, MSwipePaymentResponse, MSwipeTokenResponse } from './mswipe.service';
import { RewardsService } from './rewards.service';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  constructor(
    private mswipeService: MswipeService,
    private rewardsService: RewardsService,
    private authService: AuthService,
    private http: HttpClient
  ) {
    this.forceProductionEnvironment();
  }

  /**
   * Check if token response is valid - handles both string and boolean
   */
  private isTokenResponseValid(tokenResponse: MSwipeTokenResponse): boolean {
    if (!tokenResponse) return false;
    
    if (tokenResponse.status === 'true' || tokenResponse.status === true) {
      return true;
    }
    
    return false;
  }

  private forceProductionEnvironment() {
    this.mswipeService.setEnvironment('prod');
    console.log('🚀 FORCED PRODUCTION environment');
    console.log('🔧 Verified Environment:', this.mswipeService.getCurrentEnvironment());
  }

  /**
   * Main method to handle complete payment flow
   */
  async initiatePaymentFlow(planId?: number, amount?: number, description?: string): Promise<any> {
    try {
      console.log('🔧 Starting payment flow with valid token...');
      
      // Step 1: Create transaction
      const transactionResponse = await this.createTransaction(planId, amount, description);
      console.log('✅ Transaction created:', transactionResponse);
      
      // Step 2: Generate MSwipe token
      console.log('🔑 Calling MSwipe token generation...');
      const tokenResponse = await lastValueFrom(this.mswipeService.generateToken());
      console.log('🔑 MSwipe token response:', tokenResponse);
        
      // FIXED: Use the helper method to check token validity
      if (!tokenResponse || !this.isTokenResponseValid(tokenResponse)) {
        throw new Error('Token generation failed: ' + (tokenResponse?.msg || JSON.stringify(tokenResponse)));
      }

      this.mswipeService.setToken(tokenResponse.token);

      // Step 3: Prepare payment data
      const paymentAmount = amount || transactionResponse.amount;
      const userData = this.getUserData();
      console.log('👤 User data:', userData);

      const paymentData = {
        amount: paymentAmount.toString(),
        user_id: userData.userId.toString(),
        email_id: userData.email,
        invoice_id: transactionResponse.transaction_id,
        request_id: `REQ${Date.now()}`,
        redirect_url: `https://stories.funtelstudio.com/payment-success?txn=${transactionResponse.transaction_id}`,
        paymentreason: description || `Wallet Recharge - ₹${paymentAmount}`,
        sessiontoken: tokenResponse.token
      };

      console.log('💳 Payment data prepared:', paymentData);

      // Step 4: Initiate MSwipe payment through backend
      console.log('🔄 Initiating MSwipe payment...');
      const mswipeResponse = await lastValueFrom(this.mswipeService.initiatePayment(paymentData));
      console.log('✅ MSwipe response:', mswipeResponse);
      
      // Check response properly
      if (!mswipeResponse || mswipeResponse.status !== 'True' || !mswipeResponse.smslink) {
        const errorMsg = mswipeResponse?.responsemessage || 
                        `Payment failed with status: ${mswipeResponse?.status}` || 
                        'Payment link generation failed';
        throw new Error(errorMsg);
      }

      // Step 5: Store MSwipe response for verification
      console.log('💾 Storing MSwipe response...');
      await lastValueFrom(
        this.mswipeService.storeMSwipeResponse(transactionResponse.transaction_id, mswipeResponse)
      );

      // Step 6: Open payment popup - FIXED: Added amount parameter
      console.log('🪟 Opening payment popup...');
      return await this.openPaymentPopup(mswipeResponse.smslink, transactionResponse.transaction_id, paymentAmount);

    } catch (error: any) {
      console.error('❌ Payment flow error:', error);
      throw new Error(error.message || 'Payment initialization failed');
    }
  }

  /**
 * NEW: Initiate promotion recharge flow - FIXED VERSION
 */
async initiatePromotionRecharge(amount: number): Promise<any> {
  try {
    console.log('💰 Initiating promotion recharge for amount:', amount);

    const userData = this.getUserData();
    
    // Step 1: Initiate promotion payment using mswipe_handler.php
    const paymentResponse = await lastValueFrom(
      this.http.post<any>('https://stories.funtelstudio.com/storyapi/mswipe_handler.php', {
        action: 'initiate_promotion_payment',
        amount: amount.toString(),
        email_id: userData.email,
        environment: 'uat'
      }, { withCredentials: true })
    );

    console.log('🎯 Promotion payment response:', paymentResponse);

    if (paymentResponse.status === 'success' && paymentResponse.payment_link) {
      // Step 2: Open payment popup
      const transactionId = paymentResponse.transaction_id;
      this.openPaymentPopup(paymentResponse.payment_link, transactionId, amount, 'promotion');
      
      // SUCCESS return karo - payment successfully initiated
      return { 
        status: 'success', // ✅ Yeh change karo 'pending' se 'success' mein
        message: 'Payment initiated successfully', 
        transactionId,
        payment_link: paymentResponse.payment_link 
      };
    } else {
      throw new Error(paymentResponse.message || 'Payment initiation failed');
    }

  } catch (error: any) {
    console.error('❌ Promotion recharge error:', error);
    throw new Error('Promotion payment initiation failed: ' + error.message);
  }
}

  /**
   * Create transaction record in database
   */
  private async createTransaction(planId?: number, amount?: number, description?: string): Promise<any> {
    try {
      if (amount) {
        // Direct recharge
        return await lastValueFrom(
          this.rewardsService.initiateDirectPayment(amount, description || `Direct recharge - ₹${amount}`)
        );
      } else if (planId) {
        // Plan-based recharge
        return await lastValueFrom(this.rewardsService.initiatePayment(planId));
      } else {
        throw new Error('Either planId or amount is required');
      }
    } catch (error: any) {
      console.error('Transaction creation error:', error);
      throw new Error('Transaction creation failed: ' + (error.message || 'Unknown error'));
    }
  }

  /**
   * Open payment popup and handle response - UPDATED: Generic method for both payment types
   */
 private openPaymentPopup(paymentLink: string, transactionId: string, amount: number, paymentType: 'regular' | 'promotion' = 'regular'): void {
  const width = 500;
  const height = 700;
  const left = (screen.width - width) / 2;
  const top = (screen.height - height) / 2;
  
  const popupName = paymentType === 'promotion' ? 'MSwipePromotionPayment' : 'MSwipePayment';
  
  console.log(`🔗 Opening ${paymentType} payment popup:`, paymentLink);
  
  const popup = window.open(
    paymentLink, 
    popupName, 
    `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
  );
  
  if (!popup) {
    alert('Popup blocked! Please allow popups for this site.');
    return;
  }
  
  // Check popup status periodically
  const popupCheck = setInterval(() => {
    if (popup.closed) {
      clearInterval(popupCheck);
      console.log(`🔍 ${paymentType} payment popup closed, checking status...`);
      
      // Verify payment after popup closes based on type
      if (paymentType === 'promotion') {
        this.handlePromotionPaymentVerification(transactionId, amount);
      } else {
        this.verifyPayment(transactionId).catch(error => {
          console.error('Regular payment verification failed:', error);
        });
      }
    }
  }, 1000);
}

private async handlePromotionPaymentVerification(transactionId: string, amount: number): Promise<void> {
  try {
    console.log('🔄 Handling promotion payment verification...');
    
    // Show loading message
    alert('🔄 Verifying your promotion payment... Please wait.');
    
    // Wait for payment processing
    await new Promise(resolve => setTimeout(resolve, 5000));
    
    const verificationResult = await this.verifyPromotionPayment(transactionId, amount);
    
    if (verificationResult.status === 'success') {
      alert(`🎉 ${verificationResult.message}`);
      
      // Reload the page to update balances after 2 seconds
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      alert('⏳ Payment is being processed. Please check your transaction history in a few minutes.');
    }
    
  } catch (error: any) {
    console.error('Promotion payment handling error:', error);
    alert('❌ Payment verification failed. Please check your transaction history.');
  }
}

  /**
   * Check payment status
   */
  private async checkPaymentStatus(transactionId: string): Promise<'success' | 'failed' | 'pending'> {
    try {
      const storedResponse = await lastValueFrom(this.mswipeService.getMSwipeResponse(transactionId));
      
      if (!storedResponse?.mswipe_response) {
        return 'pending';
      }

      const mswipeResponse = storedResponse.mswipe_response;
      const transId = this.mswipeService.extractTransIdFromLink(mswipeResponse.smslink);

      if (!transId) {
        return 'pending';
      }

      const statusResponse = await lastValueFrom(this.mswipeService.getTransactionStatus(transId));
      
      if (statusResponse.Data?.[0]?.Payment_Status === 1) {
        return 'success';
      } else if (statusResponse.Data?.[0]?.Payment_Status === 0 || statusResponse.Data?.[0]?.Payment_Status === 3) {
        return 'failed';
      } else {
        return 'pending';
      }
    } catch (error) {
      console.error('Status check error:', error);
      return 'pending';
    }
  }

  /**
   * NEW: Check promotion payment status
   */
 private async checkPromotionPaymentStatus(transactionId: string): Promise<'success' | 'failed' | 'pending'> {
  try {
    // Directly check transaction status from database
    const statusResponse = await lastValueFrom(
      this.http.post<any>('https://stories.funtelstudio.com/storyapi/mswipe_handler.php', {
        action: 'get_transaction_status',
        transaction_id: transactionId
      }, { withCredentials: true })
    );

    console.log('📊 Promotion transaction status:', statusResponse);

    if (statusResponse.status === 'success') {
      const transaction = statusResponse.transaction;
      
      if (transaction.payment_status === 'completed') {
        return 'success';
      } else if (transaction.payment_status === 'failed' || transaction.payment_status === 'cancelled') {
        return 'failed';
      } else {
        return 'pending';
      }
    } else {
      return 'pending';
    }
    
  } catch (error) {
    console.error('Promotion status check error:', error);
    return 'pending';
  }
}

  /**
   * Verify payment and complete the process
   */
  private async verifyPayment(transactionId: string): Promise<any> {
    try {
      console.log('✅ Verifying payment for transaction:', transactionId);
      
      const storedResponse = await lastValueFrom(this.mswipeService.getMSwipeResponse(transactionId));
      
      if (!storedResponse?.mswipe_response) {
        throw new Error('No payment data found');
      }

      const mswipeResponse = storedResponse.mswipe_response;
      const transId = this.mswipeService.extractTransIdFromLink(mswipeResponse.smslink);

      if (!transId) {
        throw new Error('Invalid transaction ID');
      }

      const verifyData = {
        transaction_id: transactionId,
        mswipe_trans_id: mswipeResponse.txn_id,
        trans_id: transId
      };

      const verifyResponse = await lastValueFrom(
        this.rewardsService.verifyMSwipePayment(verifyData)
      );

      if (verifyResponse.status === 'success') {
        return verifyResponse;
      } else {
        throw new Error(verifyResponse.message || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      throw error;
    }
  }

  /**
   * NEW: Verify promotion payment
   */
/**
 * NEW: Verify promotion payment - SIMPLIFIED VERSION
 */
private async verifyPromotionPayment(transactionId: string, amount: number): Promise<any> {
  try {
    console.log('🔍 Verifying promotion payment:', transactionId);
    
    // Wait for payment processing
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    // Direct verification for promotion payment
    const verifyResponse = await lastValueFrom(
      this.http.post<any>('https://stories.funtelstudio.com/storyapi/verify_payment.php', {
        transaction_id: transactionId,
        recharge_type: 'promotion',
        amount: amount
      }, { withCredentials: true })
    );

    console.log('✅ Promotion payment verification:', verifyResponse);

    if (verifyResponse.status === 'success') {
      return {
        status: 'success',
        message: verifyResponse.message || `Promotion recharge successful! ₹${verifyResponse.amount_added} added to your promotion wallet.`,
        amount_added: verifyResponse.amount_added,
        new_promotion_balance: verifyResponse.new_promotion_balance
      };
    } else {
      throw new Error(verifyResponse.message || 'Payment verification failed');
    }
    
  } catch (error) {
    console.error('Payment verification error:', error);
    throw new Error('Payment status check failed: ' + (error as any).message);
  }
}

  /**
   * Get user data for payment
   */
  private getUserData(): { userId: number; email: string; mobile: string } {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error('User not logged in');
    }
    
    const currentUser = this.authService.getCurrentUserValue();
    return {
      userId: userId,
      email: currentUser?.email || 'user@example.com',
      mobile: currentUser?.mobile || '0000000000'
    };
  }
}